import { useId, useRef, useState } from 'react';
import plur from 'plur';
import type { ReactNode, RefObject } from 'react';
import { parseDate, today, getLocalTimeZone } from '@internationalized/date';
import type { CalendarDate } from '@internationalized/date';
import {
  Box,
  Button,
  DatePicker,
  Heading,
  Icon,
  InlineStack,
  Link,
  NumberField,
  PlainButton,
  Select,
  Stack,
  Tag,
  Text,
  TextField,
} from '@customerio/pluma-components/react';
import {
  OPERATORS,
  RELATIVE_UNITS,
  defaultValueFor,
  describeCondition,
  lastIncludedDay,
  opTakesList,
  opTakesValue,
  operatorsFor,
  outputNames,
} from './spec-form';
import type { FilterRow, QueryForm, SpecValue, TimeWindow } from './spec-form';
import { bareName, fieldType } from './view-catalog';
import type { FieldType, ViewCatalog } from './view-catalog';
import * as styles from './query-builder.css';

type Props = {
  form: QueryForm;
  catalog: ViewCatalog | null;
  onChange: (next: QueryForm) => void;
};

/**
 * Removing a chip or a filter row unmounts the control that had focus, which
 * drops focus to the document. Moving it to the first control left in the
 * section keeps a keyboard or screen reader user where they were working.
 */
function focusFirstControl(section: RefObject<HTMLElement | null>) {
  requestAnimationFrame(() => {
    const target = section.current?.querySelector<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    target?.focus();
  });
}

type SectionProps = {
  title: string;
  hint?: string;
  sectionRef?: RefObject<HTMLElement | null>;
  children: ReactNode;
};

/** A titled group of controls; the heading names the region for assistive tech. */
function Section({ title, hint, sectionRef, children }: SectionProps) {
  const id = useId();
  return (
    <section ref={sectionRef} aria-labelledby={id}>
      <Stack gap="100">
        <InlineStack gap="100" alignBlock="baseline">
          <Heading id={id} level="3" size="xs" weight="semibold" color="secondary">
            {title}
          </Heading>
          {hint != null ? (
            <Text size="xs" color="secondary">
              {hint}
            </Text>
          ) : null}
        </InlineStack>
        {children}
      </Stack>
    </section>
  );
}

/**
 * Chips for the members a spec reads, with a picker for the rest of the view.
 * Pluma's Tag names its own delete button "Close" for every tag, so each chip
 * carries its own remove button named after what it removes. Without a
 * catalog (a view this client does not know) chips still show and can be
 * removed; only adding is unavailable.
 */
type MemberChipsProps = {
  kind: 'metric' | 'group by';
  chosen: string[];
  available: string[];
  addLabel: string;
  minimum: number;
  onChange: (next: string[]) => void;
  sectionRef: RefObject<HTMLElement | null>;
};

function MemberChips({ kind, chosen, available, addLabel, minimum, onChange, sectionRef }: MemberChipsProps) {
  const rest = available.filter((m) => !chosen.includes(m));
  const canRemove = chosen.length > minimum;
  return (
    <div className={styles.chips}>
      {chosen.map((member) => (
        <Tag key={member} isCompact title={member}>
          {bareName(member)}
          {canRemove ? (
            <PlainButton
              className={styles.chipRemove}
              aria-label={`Remove ${kind} ${bareName(member)}`}
              onClick={() => {
                onChange(chosen.filter((m) => m !== member));
                focusFirstControl(sectionRef);
              }}
            >
              <Icon name="close" size="sm" />
            </PlainButton>
          ) : null}
        </Tag>
      ))}
      {rest.length > 0 ? (
        <Box className={styles.pickerField}>
          <Select
            size="sm"
            ariaLabel={addLabel}
            placeholder={`+ ${addLabel}`}
            value={null}
            isSearchable
            options={rest.map((m) => ({ value: m, label: bareName(m), description: m }))}
            onChange={(value) => {
              if (value != null) onChange([...chosen, value]);
            }}
          />
        </Box>
      ) : null}
    </div>
  );
}

function toCalendarDate(iso: string): CalendarDate | null {
  try {
    return iso === '' ? null : parseDate(iso);
  } catch {
    return null;
  }
}

type TimeWindowEditorProps = {
  time: TimeWindow;
  catalog: ViewCatalog | null;
  onChange: (next: TimeWindow) => void;
};

function TimeWindowEditor({ time, catalog, onChange }: TimeWindowEditorProps) {
  if (time.mode === 'none') {
    const defaultField = catalog?.dateFields[0] ?? null;
    return (
      <InlineStack gap="100" alignBlock="center" wrap>
        <Text size="sm" color="secondary">
          No time window
        </Text>
        {defaultField != null ? (
          <Link onClick={() => onChange({ mode: 'relative', field: defaultField, n: 30, unit: 'day' })}>
            Limit to the last 30 days
          </Link>
        ) : null}
      </InlineStack>
    );
  }

  if (time.mode === 'relative') {
    const n = time.n;
    return (
      <Stack gap="100">
        <InlineStack gap="100" alignBlock="center" wrap>
          <Text size="sm">In the last</Text>
          <Box className={styles.narrowField}>
            <NumberField
              size="sm"
              ariaLabel="Number of days, weeks or months"
              min={1}
              value={n}
              withControls={false}
              onChange={(value) => onChange({ ...time, n: value ?? 1 })}
            />
          </Box>
          <Box className={styles.narrowField}>
            <Select
              size="sm"
              ariaLabel="Unit"
              value={time.unit}
              options={RELATIVE_UNITS.map((unit) => ({ value: unit, label: plur(unit, n) }))}
              onChange={(value) => {
                if (value != null) onChange({ ...time, unit: value });
              }}
            />
          </Box>
          <Text size="sm" color="secondary">
            of {bareName(time.field)}
          </Text>
        </InlineStack>
        <InlineStack gap="200" wrap>
          <Link
            onClick={() => {
              const end = today(getLocalTimeZone());
              const days = n * ({ day: 1, week: 7, month: 30 }[time.unit] ?? 1);
              onChange({
                mode: 'absolute',
                field: time.field,
                from: end.subtract({ days }).toString(),
                to: end.toString(),
                toOp: '<=',
              });
            }}
          >
            Use exact dates instead
          </Link>
          <Link onClick={() => onChange({ mode: 'none' })}>Remove time window</Link>
        </InlineStack>
      </Stack>
    );
  }

  const start = toCalendarDate(time.from);
  // The picker shows an inclusive range, so a half-open bound (`<`) shows the
  // last day that is in. A range the customer picks is written back as they
  // see it, inclusive.
  const lastDay = toCalendarDate(lastIncludedDay(time));
  // A degenerate spec (an empty or inverted range) must not hand the picker an
  // end before its start; the spec itself is left as it is until a pick.
  const end = start != null && lastDay != null && lastDay.compare(start) < 0 ? start : lastDay;
  return (
    <Stack gap="100">
      <InlineStack gap="100" alignBlock="center" wrap>
        <Text size="sm">Between</Text>
        <Box className={styles.dateRangeField}>
          <DatePicker
            size="sm"
            ariaLabel={`Date range for ${bareName(time.field)}`}
            isRange
            value={start != null && end != null ? { start, end } : null}
            onChange={(value) => {
              // A range picker hands back {start, end}; anything else means cleared.
              const range = value != null && 'start' in value && 'end' in value ? value : null;
              onChange({
                ...time,
                from: range?.start?.toString() ?? '',
                to: range?.end?.toString() ?? '',
                toOp: '<=',
              });
            }}
          />
        </Box>
        <Text size="sm" color="secondary">
          on {bareName(time.field)}
        </Text>
      </InlineStack>
      <InlineStack gap="200" wrap>
        <Link onClick={() => onChange({ mode: 'relative', field: time.field, n: 30, unit: 'day' })}>
          Use a rolling window instead
        </Link>
        <Link onClick={() => onChange({ mode: 'none' })}>Remove time window</Link>
      </InlineStack>
    </Stack>
  );
}

type FilterValueProps = {
  row: FilterRow;
  label: string;
  catalog: ViewCatalog | null;
  onChange: (value: SpecValue) => void;
};

/** A list value as the customer types it: comma-separated, numbers for number fields. */
function parseList(text: string, type: FieldType): SpecValue {
  const parts = text
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s !== '');
  if (type !== 'number') return parts;
  return parts.map(Number).filter((n) => Number.isFinite(n));
}

type ListValueFieldProps = {
  initial: SpecValue | undefined;
  label: string;
  type: FieldType;
  onChange: (value: SpecValue) => void;
};

/**
 * A comma-separated list. The text is the field's own: writing the parsed
 * list back into the input on each keystroke would eat the separator the
 * customer just typed. The parent remounts it (by key) when the row's field or
 * operator changes, and the modal remounts the whole form on Reset: the only
 * times the value changes from outside.
 */
function ListValueField({ initial, label, type, onChange }: ListValueFieldProps) {
  const [text, setText] = useState(() => (Array.isArray(initial) ? initial.join(', ') : ''));
  return (
    <TextField
      size="sm"
      ariaLabel={`${label}, comma-separated`}
      placeholder="one, two, three"
      value={text}
      onInput={(event) => {
        setText(event.target.value);
        onChange(parseList(event.target.value, type));
      }}
    />
  );
}

function FilterValue({ row, label, catalog, onChange }: FilterValueProps) {
  const type = fieldType(catalog, row.field);
  const options = catalog?.enums[bareName(row.field)];

  if (!opTakesValue(row.op)) return <Box />;

  if (opTakesList(row.op)) {
    return (
      <ListValueField
        key={`${row.field}|${row.op}`}
        initial={row.value}
        label={label}
        type={type}
        onChange={onChange}
      />
    );
  }

  if (type === 'enum' && options != null) {
    return (
      <Select
        size="sm"
        ariaLabel={label}
        value={typeof row.value === 'string' ? row.value : null}
        options={options.map((o) => ({ value: o, label: o }))}
        onChange={(value) => {
          if (value != null) onChange(value);
        }}
      />
    );
  }
  if (type === 'bool') {
    return (
      <Select
        size="sm"
        ariaLabel={label}
        value={row.value === false ? 'false' : 'true'}
        options={[
          { value: 'true', label: 'true' },
          { value: 'false', label: 'false' },
        ]}
        onChange={(value) => onChange(value === 'true')}
      />
    );
  }
  if (type === 'number') {
    return (
      <NumberField
        size="sm"
        ariaLabel={label}
        withControls={false}
        value={typeof row.value === 'number' ? row.value : undefined}
        onChange={(value) => onChange(value ?? 0)}
      />
    );
  }
  return (
    <TextField
      size="sm"
      ariaLabel={label}
      type={type === 'date' ? 'date' : 'text'}
      value={typeof row.value === 'string' ? row.value : Array.isArray(row.value) ? row.value.join(', ') : ''}
      onInput={(event) => onChange(event.target.value)}
    />
  );
}

function FiltersEditor({ form, catalog, onChange, sectionRef }: Props & { sectionRef: RefObject<HTMLElement | null> }) {
  // Dimensions filter inside the view by their dotted name; metrics filter the
  // outer query by their bare output name, the only form a metric filter takes.
  const fieldOptions = [
    ...(catalog?.dimensions ?? []).map((d) => ({ value: d, label: bareName(d), description: d })),
    ...(catalog?.metrics ?? []).map((m) => ({ value: bareName(m), label: bareName(m), description: `${m} (output)` })),
  ];
  // A field the agent used that the catalog does not list stays selectable as written.
  for (const row of form.filters) {
    if (row.field !== '' && !fieldOptions.some((o) => o.value === row.field)) {
      fieldOptions.push({ value: row.field, label: bareName(row.field), description: row.field });
    }
  }

  const update = (index: number, next: FilterRow) => {
    const filters = form.filters.map((row, i) => (i === index ? next : row));
    onChange({ ...form, filters });
  };

  return (
    <Stack gap="100">
      {form.filters.map((row, index) => {
        const type = fieldType(catalog, row.field);
        const ops = operatorsFor(type, row.op);
        // Every row has the same three controls, so each name carries the row
        // number and field: "Filter 2 field", not three controls named "Field".
        const rowName = `Filter ${index + 1}`;
        const fieldName = row.field === '' ? rowName : `${rowName}, ${bareName(row.field)}`;
        return (
          <div key={index} role="group" aria-label={fieldName} className={styles.filterRow}>
            <Select
              size="sm"
              ariaLabel={`${rowName} field`}
              isSearchable
              value={row.field}
              options={fieldOptions}
              onChange={(value) => {
                if (value == null) return;
                const nextType = fieldType(catalog, value);
                update(index, {
                  field: value,
                  op: OPERATORS[nextType][0].op,
                  value: defaultValueFor(nextType, catalog?.enums[bareName(value)]),
                });
              }}
            />
            <Select
              size="sm"
              ariaLabel={`${fieldName} operator`}
              value={row.op}
              options={ops.map((o) => ({ value: o.op, label: o.label }))}
              onChange={(value) => {
                if (value == null) return;
                // A list operator needs a list value and vice versa; keep the
                // value when the shape still fits, start over when it does not.
                const wasList = Array.isArray(row.value);
                const keep = opTakesList(value) === wasList || !opTakesValue(value);
                const fresh = opTakesList(value) ? [] : defaultValueFor(type, catalog?.enums[bareName(row.field)]);
                update(index, { ...row, op: value, value: keep ? row.value : fresh });
              }}
            />
            <FilterValue
              row={row}
              label={`${fieldName} value`}
              catalog={catalog}
              onChange={(value) => update(index, { ...row, value })}
            />
            <Button
              size="sm"
              variant="tertiary"
              icon="close"
              isIconOnly
              aria-label={`Remove ${fieldName.toLowerCase()}`}
              onClick={() => {
                onChange({ ...form, filters: form.filters.filter((_, i) => i !== index) });
                focusFirstControl(sectionRef);
              }}
            />
          </div>
        );
      })}

      {form.opaque.map((condition, index) => {
        const description = describeCondition(condition);
        return (
          <InlineStack key={`opaque-${index}`} gap="100" alignBlock="center" wrap>
            <Tag isCompact>
              {description}
              <PlainButton
                className={styles.chipRemove}
                aria-label={`Remove grouped condition ${description}`}
                onClick={() => {
                  onChange({ ...form, opaque: form.opaque.filter((_, i) => i !== index) });
                  focusFirstControl(sectionRef);
                }}
              >
                <Icon name="close" size="sm" />
              </PlainButton>
            </Tag>
            <Text size="xs" color="secondary">
              Grouped condition: keep it as is or remove it
            </Text>
          </InlineStack>
        );
      })}

      {fieldOptions.length > 0 ? (
        <Box className={styles.pickerField}>
          <Select
            size="sm"
            ariaLabel="Add a filter"
            placeholder="+ add filter"
            isSearchable
            value={null}
            options={fieldOptions}
            onChange={(value) => {
              if (value == null) return;
              const type = fieldType(catalog, value);
              onChange({
                ...form,
                filters: [
                  ...form.filters,
                  {
                    field: value,
                    op: OPERATORS[type][0].op,
                    value: defaultValueFor(type, catalog?.enums[bareName(value)]),
                  },
                ],
              });
            }}
          />
        </Box>
      ) : null}
    </Stack>
  );
}

function SortEditor({ form, onChange }: Pick<Props, 'form' | 'onChange'>) {
  const names = outputNames(form);
  // Terms past the first still sort the result, so they are shown and can be
  // removed, even though only the first is edited here.
  const moreSort = form.moreSort.filter((o) => names.includes(o.field) && o.field !== form.sort?.field);
  return (
    <Stack gap="100">
      <InlineStack gap="100" alignBlock="center" wrap>
        <Text size="sm">Sort by</Text>
        <Box className={styles.pickerField}>
          <Select
            size="sm"
            ariaLabel="Sort by"
            value={form.sort != null && names.includes(form.sort.field) ? form.sort.field : ''}
            options={[{ value: '', label: 'no sort' }, ...names.map((n) => ({ value: n, label: n }))]}
            onChange={(value) =>
              onChange({
                ...form,
                sort: value == null || value === '' ? null : { field: value, dir: form.sort?.dir ?? 'desc' },
              })
            }
          />
        </Box>
        <Box className={styles.directionField}>
          <Select
            size="sm"
            ariaLabel="Sort direction"
            isDisabled={form.sort == null}
            value={form.sort?.dir ?? 'desc'}
            options={[
              { value: 'desc', label: 'highest first' },
              { value: 'asc', label: 'lowest first' },
            ]}
            onChange={(value) => {
              if (form.sort != null && (value === 'asc' || value === 'desc'))
                onChange({ ...form, sort: { ...form.sort, dir: value } });
            }}
          />
        </Box>
        <Text size="sm" color="secondary">
          · show
        </Text>
        <Box className={styles.narrowField}>
          <NumberField
            size="sm"
            ariaLabel="Row limit, empty for all rows"
            placeholder="all"
            min={1}
            max={10000}
            withControls={false}
            value={form.limit ?? undefined}
            onChange={(value) => onChange({ ...form, limit: value == null || value <= 0 ? null : Math.floor(value) })}
          />
        </Box>
        <Text size="sm" color="secondary">
          rows
        </Text>
      </InlineStack>
      {moreSort.length > 0 ? (
        <InlineStack gap="100" alignBlock="center" wrap>
          <Text size="sm" color="secondary">
            then by
          </Text>
          {moreSort.map((term) => (
            <Tag key={term.field} isCompact>
              {term.field} {term.dir.toLowerCase() === 'asc' ? '↑' : '↓'}
              <PlainButton
                className={styles.chipRemove}
                aria-label={`Remove sort by ${term.field}`}
                onClick={() => onChange({ ...form, moreSort: form.moreSort.filter((o) => o.field !== term.field) })}
              >
                <Icon name="close" size="sm" />
              </PlainButton>
            </Tag>
          ))}
        </InlineStack>
      ) : null}
    </Stack>
  );
}

/**
 * The form that edits a query spec. Every control writes the spec; the SQL is
 * never assembled here, the server compiles it from the spec on each change.
 */
export function QueryBuilderForm({ form, catalog, onChange }: Props) {
  const metricsRef = useRef<HTMLElement>(null);
  const dimensionsRef = useRef<HTMLElement>(null);
  const filtersRef = useRef<HTMLElement>(null);

  const groupNote =
    form.groupBy != null
      ? [
          ...(form.groupBy.columns ?? []),
          ...(form.groupBy.time_bucket != null
            ? [`${form.groupBy.time_bucket.field} by ${form.groupBy.time_bucket.unit}`]
            : []),
        ].join(', ')
      : null;

  return (
    <Stack gap="300">
      <Section title="Measure" hint="Metrics from the semantic view." sectionRef={metricsRef}>
        <MemberChips
          kind="metric"
          chosen={form.metrics}
          available={catalog?.metrics ?? []}
          addLabel="add metric"
          minimum={1}
          onChange={(metrics) => onChange({ ...form, metrics })}
          sectionRef={metricsRef}
        />
      </Section>

      <Section title="Group by" sectionRef={dimensionsRef}>
        <InlineStack gap="100" alignBlock="center" wrap>
          {form.dimensions.length === 0 ? (
            <Text size="sm" color="secondary">
              Totals only
            </Text>
          ) : null}
          <MemberChips
            kind="group by"
            chosen={form.dimensions}
            available={catalog?.dimensions ?? []}
            addLabel="group by"
            minimum={0}
            onChange={(dimensions) => onChange({ ...form, dimensions })}
            sectionRef={dimensionsRef}
          />
        </InlineStack>
        {groupNote != null && groupNote !== '' ? (
          <Text size="xs" color="secondary">
            Re-aggregated over {groupNote}; metrics are summed.
          </Text>
        ) : null}
      </Section>

      <Section title="Time window">
        <TimeWindowEditor time={form.time} catalog={catalog} onChange={(time) => onChange({ ...form, time })} />
      </Section>

      <Section title="Filters" sectionRef={filtersRef}>
        <FiltersEditor form={form} catalog={catalog} onChange={onChange} sectionRef={filtersRef} />
      </Section>

      <Section title="Sort and limit">
        <SortEditor form={form} onChange={onChange} />
      </Section>
    </Stack>
  );
}
